import React, { useState, useEffect } from "react";
import "../styles/ManageMember.css";
import SideBar from "../components/SideBar";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useLocation, useNavigate } from "react-router-dom";
import { itAdminService } from "../services/itAdminService";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
});

function ManageITAdmin() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("success");
  const [currentPage, setCurrentPage] = useState(1);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [totalAdmins, setTotalAdmins] = useState(0);
  const [filteredAdminsCount, setFilteredAdminsCount] = useState(0);
  const [allAdmins, setAllAdmins] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);

  // Password reset modal state
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllAdmins();
  }, []);

  useEffect(() => {
    if (location.state && location.state.message) {
      setSnackMessage(location.state.message);
      setSnackSeverity("success");
      setSnackOpen(true);
    }
  }, [location.state]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, status]);

  useEffect(() => {
    const fetchData = async () => {
      if (search || status !== "all") {
        setIsFiltering(true);
        await fetchFilteredAdmins();
      } else {
        setIsFiltering(false);
      }
    };
    fetchData();
  }, [search, status, currentPage]);

  useEffect(() => {
    if (!isFiltering && allAdmins.length > 0) {
      const formattedAdmins = formatAdmins(allAdmins);
      const filtered = applyFilters(formattedAdmins);
      setFilteredAdminsCount(filtered.length);
      
      const itemsPerPage = 10;
      const startIndex = (currentPage - 1) * itemsPerPage;
      const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);
      setAdmins(paginated);
    }
  }, [allAdmins, currentPage, isFiltering, search, status]);

  const fetchAllAdmins = async () => {
    try {
      setLoading(true);
      const response = await itAdminService.getAllItAdmins({
        page: 1,
        per_page: 1000,
      });

      if (response.users && Array.isArray(response.users)) {
        setAllAdmins(response.users);
        setTotalAdmins(response.pagination?.total || response.users.length);
      }
    } catch (error) {
      console.error("Error fetching all IT admins:", error);
      setSnackMessage("Failed to fetch IT admins.");
      setSnackSeverity("error");
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredAdmins = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: 10,
        ...(search && { search }),
        ...(status !== "all" && { status: status.toLowerCase() }),
      };

      const response = await itAdminService.getAllItAdmins(params);

      if (response.users && Array.isArray(response.users)) {
        const formatted = formatAdmins(response.users);
        setAdmins(formatted);
        setFilteredAdminsCount(response.pagination?.total || response.users.length);
      }
    } catch (error) {
      console.error("Error fetching filtered IT admins:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (adminsList) => {
    return adminsList.filter((admin) => {
      const matchesSearch =
        search === "" ||
        admin.fullName.toLowerCase().includes(search.toLowerCase()) ||
        admin.email.toLowerCase().includes(search.toLowerCase()) ||
        admin.phone.toLowerCase().includes(search.toLowerCase()) ||
        String(admin.id).includes(search);

      const matchesStatus =
        status === "all" || admin.status.toLowerCase() === status.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  };

  const formatAdmins = (adminsData) => {
    return adminsData.map((admin) => ({
      id: admin.id || admin.user_id,
      fullName: `${admin.first_name || ""} ${admin.last_name || ""}`.trim() || "N/A",
      email: admin.email || "N/A",
      phone: admin.phone_number || "N/A",
      city: admin.city || "N/A",
      status: admin.user_status
        ? admin.user_status.charAt(0).toUpperCase() + admin.user_status.slice(1)
        : "Active",
      createdAt: admin.created_at ? new Date(admin.created_at).toLocaleDateString() : "N/A",
      rawStatus: admin.user_status || "active",
    }));
  };

  const handleToggleStatus = async (admin) => {
    const newStatus = admin.rawStatus === "active" ? "suspended" : "active";
    setActionLoading(true);
    try {
      await itAdminService.updateItAdminStatus(admin.id, newStatus, `Status updated by Super Admin to ${newStatus}`);
      setSnackMessage(`IT user status changed to ${newStatus}.`);
      setSnackSeverity("success");
      setSnackOpen(true);
      fetchAllAdmins();
    } catch (err) {
      console.error("Status update error:", err);
      setSnackMessage("Failed to update status.");
      setSnackSeverity("error");
      setSnackOpen(true);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 8) {
      setSnackMessage("Password must be at least 8 characters.");
      setSnackSeverity("error");
      setSnackOpen(true);
      return;
    }

    setActionLoading(true);
    try {
      await itAdminService.resetItAdminPassword(selectedAdmin.id, newPassword);
      setSnackMessage("Password reset successfully.");
      setSnackSeverity("success");
      setSnackOpen(true);
      setShowPasswordModal(false);
      setNewPassword("");
      setSelectedAdmin(null);
    } catch (err) {
      console.error("Password reset error:", err);
      setSnackMessage("Failed to reset password.");
      setSnackSeverity("error");
      setSnackOpen(true);
    } finally {
      setActionLoading(false);
    }
  };

  const goToCreateAccount = () => {
    navigate("/create-it-admin");
  };

  const handleCloseSnack = () => {
    setSnackOpen(false);
  };

  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredAdminsCount / itemsPerPage) || 1;
  const currentAdmins = isFiltering 
    ? admins 
    : applyFilters(formatAdmins(allAdmins)).slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{ border: 0, boxShadow: "none" }}>
          <div className="header-top">
            <h2 className="header-title">Manage IT Users</h2>
            <button className="action-btn" onClick={goToCreateAccount} >
              + Add New IT User
            </button>
          </div>

          <div className="user-statistics">
            <div className="stat-item">
              <span className="stat-label">Total IT Users:</span>
              <span className="stat-value">{totalAdmins}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Filtered Users:</span>
              <span className="stat-value">{filteredAdminsCount}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Current Page:</span>
              <span className="stat-value">{currentAdmins.length}</span>
            </div>
          </div>

          <div className="filters">
            {/* <div className="input-group">
              <label>Filter by status:</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="filter-select"
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="inactive">Inactive</option>
              </select>
            </div> */}

            <div className="input-group" style={{ flexGrow: 1 }}>
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by ID, name, or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
                maxLength={30}
              />
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="member-table">
            <thead>
              <tr>
                <th style={{ width: "60px" }}>ID</th>
                <th style={{ width: "220px" }}>Full Name</th>
                <th style={{ width: "240px" }}>Email</th>
                <th style={{ width: "160px" }}>Phone</th>
                <th style={{ width: "120px" }}>City</th>
                <th style={{ width: "120px" }}>Joined</th>
                <th style={{ width: "110px" }}>Status</th>
                {/* <th style={{ width: "180px", textAlign: "center" }}>Actions</th> */}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>
                    <div className="loading-message">Loading IT Users...</div>
                  </td>
                </tr>
              ) : currentAdmins.length > 0 ? (
                currentAdmins.map((admin, i) => (
                  <tr key={admin.id || i}>
                    <td>{admin.id}</td>
                    <td>{admin.fullName}</td>
                    <td>{admin.email}</td>
                    <td>{admin.phone}</td>
                    <td>{admin.city}</td>
                    <td>{admin.createdAt}</td>
                    <td>
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "bold",
                          background: admin.rawStatus === "active" ? "#E8F5E9" : "#FFEBEE",
                          color: admin.rawStatus === "active" ? "#2E7D32" : "#C62828",
                        }}
                      >
                        {admin.status}
                      </span>
                    </td>
                    {/* <td style={{ textAlign: "center" }}>
                      <button
                        onClick={() => handleToggleStatus(admin)}
                        disabled={actionLoading}
                        style={{
                          padding: "5px 10px",
                          marginRight: "6px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                          background: admin.rawStatus === "active" ? "#fff3e0" : "#e8f5e9",
                          color: admin.rawStatus === "active" ? "#e65100" : "#2e7d32",
                          cursor: "pointer",
                          fontSize: "12px"
                        }}
                      >
                        {admin.rawStatus === "active" ? "Suspend" : "Activate"}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedAdmin(admin);
                          setShowPasswordModal(true);
                        }}
                        style={{
                          padding: "5px 10px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                          background: "#f0f4f8",
                          color: "#19224d",
                          cursor: "pointer",
                          fontSize: "12px"
                        }}
                      >
                        Reset Pwd
                      </button>
                    </td> */}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center", padding: "30px" }}>
                    No IT users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination" style={{ display: "flex", justifyContent: "center", gap: "10px", margin: "20px 0" }}>
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{ padding: "6px 14px", cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
            >
              Previous
            </button>
            <span style={{ alignSelf: "center", fontWeight: "bold" }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              style={{ padding: "6px 14px", cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Password Reset Modal */}
      {showPasswordModal && selectedAdmin && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", padding: "24px", borderRadius: "8px", width: "400px", maxWidth: "90%" }}>
            <h3 style={{ margin: "0 0 16px 0", color: "#19224D" }}>Reset Password for {selectedAdmin.fullName}</h3>
            <form onSubmit={handleResetPassword}>
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", marginBottom: "6px", fontWeight: "bold", fontSize: "14px" }}>New Password:</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  required
                  style={{ width: "100%", padding: "8px 12px", border: "1px solid #ccc", borderRadius: "4px" }}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setSelectedAdmin(null);
                    setNewPassword("");
                  }}
                  style={{ padding: "8px 16px", borderRadius: "4px", border: "1px solid #ccc", background: "#f5f5f5", cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  style={{ padding: "8px 16px", borderRadius: "4px", border: "none", background: "#19224D", color: "#fff", cursor: actionLoading ? "not-allowed" : "pointer" }}
                >
                  {actionLoading ? "Updating..." : "Save Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnack}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnack} severity={snackSeverity} sx={{ width: "100%" }}>
          {snackMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default ManageITAdmin;
